import {
  useV2CreateCurrencyMutation,
  useV2UpdateCurrencyMutation,
  useV2DeleteCurrencyMutation,
} from "@generated";
import type { V2CurrencyResource } from "@generated";
import type { CurrencyFormValues } from "../types";

// Build the JSON:API `data` body for create/update. The `account` relationship
// required by V2CurrencyResource is inferred from auth server-side, so we omit
// it and cast (same pattern as the BusinessUnits/Clients MSPAs). A default
// currency always has a conversion rate of 1 (the server enforces this too).
export const buildCurrencyData = (
  values: CurrencyFormValues,
  id?: string
): V2CurrencyResource => {
  const conversionRate = values.default ? 1 : values.conversionRate ?? 0;

  return {
    type: "currencies",
    ...(id ? { id } : {}),
    attributes: {
      name: values.name,
      "currency-type": values.currencyType,
      "conversion-rate": conversionRate,
      default: values.default,
    },
  } as unknown as V2CurrencyResource;
};

export const useCurrencyMutations = () => {
  const [create, createState] = useV2CreateCurrencyMutation();
  const [update, updateState] = useV2UpdateCurrencyMutation();
  const [destroy, destroyState] = useV2DeleteCurrencyMutation();

  return {
    create: (values: CurrencyFormValues) =>
      create({ body: { data: buildCurrencyData(values) } }).unwrap(),

    update: (id: string, values: CurrencyFormValues) =>
      update({
        id: id,
        body: { data: buildCurrencyData(values, id) },
      }).unwrap(),

    // Archive (soft-delete: the API stamps deleted_at; restore re-activates).
    archive: (id: string) => destroy({ id: id }).unwrap(),

    // `active` is not in the generated V2CurrencyResource schema; the cast lets
    // restore re-activate a soft-deleted currency (same pattern as BusinessUnits).
    restore: (id: string, name: string) =>
      update({
        id: id,
        body: {
          data: {
            type: "currencies",
            id: id,
            attributes: { name, active: true },
          } as unknown as V2CurrencyResource,
        },
      }).unwrap(),

    states: { createState, updateState, destroyState },
  };
};

export default useCurrencyMutations;
